import { and, desc, eq, getTableColumns, isNull, sql } from "drizzle-orm";

import type { WsTemplate } from "@storyboard/web-client";
import { bulkInsert } from "@/db/bulkInsert";
import { db } from "@/db/db";
import { account, template, template as templateTable, transcription, workspaceMembership } from "@/db/schema";
import { deduplicateByKeyValue } from "@/models/appSyncFunctions.ts";
import { emptyQuery } from "@/models/emptyQuery.ts";
import { emitTableUpdates } from "@/models/eventing/TableEventManager";
import { logQuery } from "@/models/logQuery.ts";

function transformWsTemplate(r: WsTemplate) {
  return {
    id: r.id,
    name: r.name,
    template: r.template,
    workspaceId: r.workspaceId,
    authorId: r.authorId || null,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
    deletedAt: r.deletedAt || null,
  };
}

export async function deleteTemplateById(id: string): Promise<void> {
  await db.delete(templateTable).where(eq(templateTable.id, id)).execute();
  emitTableUpdates(["template"]);
}

export async function bulkUpsertTemplate(records: WsTemplate[]) {
  if (records.length === 0) return;
  const data = records.map((c) => transformWsTemplate(c));
  const deduped = deduplicateByKeyValue(data, (item) => item.id);
  await bulkInsert(template, deduped, {
    onConflict: {
      type: "doUpdate",
      target: [template.id],
      set: {
        name: sql`"excluded"."name"`,
        template: sql`"excluded"."template"`,
        workspaceId: sql`"excluded"."workspaceId"`,
        authorId: sql`"excluded"."authorId"`,
        updatedAt: sql`"excluded"."updatedAt"`,
        deletedAt: sql`"excluded"."deletedAt"`,
      },
    },
  });
  emitTableUpdates(["template"]);
}

export function cachedTemplatesSelect({ workspaceId }: { workspaceId?: string }) {
  const query = db
    .select({
      ...getTableColumns(template),
      authorName: account.name,
      role: workspaceMembership.role,
    })
    .from(template)
    .innerJoin(account, eq(template.authorId, account.id))
    .innerJoin(
      workspaceMembership,
      and(eq(workspaceMembership.accountId, account.id), eq(workspaceMembership.workspaceId, workspaceId)),
    )
    .where(and(eq(template.workspaceId, workspaceId), isNull(template.deletedAt)))
    .orderBy(desc(template.createdAt));

  if (!workspaceId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Cached Templates", query);
  return query;
}
